import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import difference from 'lodash/difference'
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import reject from 'lodash/reject'
import uniq from 'lodash/uniq'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useMemo } from 'react'
import {
  getExcludedClusters,
  useEnhancedProductStatus
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import ChipListSkeleton from '@modules/order/components/CellLoaders/ChipListSkeleton/ChipListSkeleton'
import { makeStyles } from '@material-ui/core'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import { EnhancedChipSelect, EnhancedRMClusterChip } from './EnhancedRMClusterChip'

const useStyles = makeStyles(theme => ({
  chipListContainer: {
    height: 240,
    margin: '16px 0px',
    padding: '0px 12px',
    overflowX: 'hidden',
    overflowY: 'auto'
  },
  chip: {
    width: 110,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '24px',
    padding: 0,
    position: 'relative',
    borderRadius: '12px'
  },
  colorItem: {
    position: 'absolute',
    left: '7px',
    marginRight: '5px'
  },
  chipAction: {
    position: 'absolute',
    right: '-2px',
    marginRight: '5px'
  },
  content: {
    left: '22px',
    position: 'absolute',
    maxWidth: 'calc(100% - 36px)'
  }
}))

const EnhancedRMClusters: FC<CellItem> = ({ rowData, column }) =>
  useObserver(function useHook() {
    const {
      uiStore: {
        dataStore: { summary },
        isOrderEditable
      }
    } = useOrderContext()
    const { uiStore } = useViewContext()
    const classes = useStyles()
    const { bookUnbookDoors } = uiStore as EnhancedListUIStore<any>
    const { doorOrderProducts = {} } = rowData || {}

    const clusters = useMemo(() => {
      const uniqueClusters = map(summary.doorOrders, 'door.cluster').sort(
        (a, b) => a.sortOrder - b.sortOrder
      )
      // Sort the unique clusters based on door.cluster.sortOrder
      return reject(uniq(map(uniqueClusters, 'name')), isEmpty)
    }, [summary?.doorOrders])

    const excludedClusters = useMemo(
      () => getExcludedClusters(rowData?.excludedInDoors, summary?.doorOrders),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [summary.doorOrders, rowData?.excludedInDoors]
    )

    const clusterColorMap = useMemo(
      () =>
        reduce(
          summary?.doorOrders,
          (acc, doorOrder) => ({
            ...acc,
            [doorOrder.door.cluster.name]: doorOrder.door.cluster.color
          }),
          {}
        ),
      [summary?.doorOrders]
    )
    const selectedClusters = useMemo(() => {
      const uniqueClusters = map(
        filter(summary?.doorOrders, doorOrder => doorOrderProducts[doorOrder?.door?.id]),
        'door.cluster'
      ).sort((a, b) => a.sortOrder - b.sortOrder)
      // Sort the unique clusters based on door.cluster.sortOrder
      return reject(uniq(map(uniqueClusters, 'name')), isEmpty)
    }, [summary?.doorOrders, doorOrderProducts])

    const itemTemplate = useMemo(
      () => EnhancedRMClusterChip({ colorMap: clusterColorMap, classes }),
      [clusterColorMap, classes]
    )
    const isProductActive: boolean = useEnhancedProductStatus(rowData)

    if (!rowData) {
      return (
        <div>
          <ChipListSkeleton count={3} />
        </div>
      )
    }
    return (
      <div className={classes.chipListContainer}>
        <EnhancedChipSelect
          disabledOptions={excludedClusters}
          options={clusters}
          disabled={!isOrderEditable || !isProductActive}
          selected={selectedClusters}
          itemTemplate={itemTemplate}
          onSelect={({ selected }) => {
            const addedClusters = new Set(difference(selected, selectedClusters))
            if (addedClusters.size) {
              bookUnbookDoors(
                summary.clientOrder.clientOrderId,
                rowData.id,
                map(
                  filter(summary.doorOrders, (doorOrder: any) =>
                    addedClusters.has(doorOrder?.door?.cluster.name)
                  ),
                  'id'
                ),
                true,
                map(
                  filter(summary.doorOrders, (doorOrder: any) =>
                    addedClusters.has(doorOrder?.door?.cluster.name)
                  ),
                  doorOrder => doorOrder?.door?.id
                )
              )
            } else {
              const removedClusters = new Set(difference(selectedClusters, selected))
              bookUnbookDoors(
                summary.clientOrder.clientOrderId,
                rowData.id,
                map(
                  filter(summary.doorOrders, doorOrder =>
                    removedClusters.has(doorOrder?.door?.cluster.name)
                  ),
                  'id'
                ),
                false,
                map(
                  filter(summary.doorOrders, (doorOrder: any) =>
                    removedClusters.has(doorOrder?.door?.cluster.name)
                  ),
                  doorOrder => doorOrder?.door?.id
                )
              )
            }
          }}
        />
      </div>
    )
  })

export default EnhancedRMClusters
